"use client";

import type { LucideIcon } from "lucide-react";
import { usePathname } from "next/navigation";
import * as React from "react";
import type { BreadcrumbItem } from "./Breadcrumb";

// The sidebar publishes its navigation tree here so every page header can derive
// its breadcrumb trail (section icon, then ancestors) from the URL alone.

export type NavTrailItem = {
  title: string;
  url: string | null;
  icon?: LucideIcon | null;
  activeUrls?: string[];
  subItems?: NavTrailItem[];
};

type NavTrailSource = { prefix: string; items: NavTrailItem[] };

const NavTrailContext = React.createContext<{
  source: NavTrailSource | null;
  setSource: (source: NavTrailSource | null) => void;
} | null>(null);

export function NavTrailProvider({ children }: { children: React.ReactNode }) {
  const [source, setSource] = React.useState<NavTrailSource | null>(null);
  const value = React.useMemo(() => ({ source, setSource }), [source]);
  return <NavTrailContext.Provider value={value}>{children}</NavTrailContext.Provider>;
}

export function useRegisterNavTrail(prefix: string, items: NavTrailItem[]) {
  const setSource = React.useContext(NavTrailContext)?.setSource;
  React.useEffect(() => {
    setSource?.({ prefix, items });
  }, [setSource, prefix, items]);
}

function pathOf(url: string) {
  return (url.split("?")[0] ?? "").replace(/^\/+|\/+$/gu, "");
}

function matchLength(path: string, item: NavTrailItem) {
  const candidates = [item.url, ...(item.activeUrls ?? [])].filter((url): url is string => url != null).map(pathOf);
  return Math.max(-1, ...candidates.map((url) => (path === url || (url && path.startsWith(`${url}/`)) ? url.length : -1)));
}

function findChain(path: string, items: NavTrailItem[]): { chain: NavTrailItem[]; exact: boolean } | null {
  let best: { chain: NavTrailItem[]; exact: boolean; length: number } | null = null;
  for (const item of items) {
    const length = matchLength(path, item);
    const child = item.subItems?.length ? findChain(path, item.subItems) : null;
    if (child) {
      const childLength = matchLength(path, child.chain[child.chain.length - 1]!);
      if (!best || childLength > best.length) best = { chain: [item, ...child.chain], exact: child.exact, length: childLength };
    }
    if (length >= 0 && (!best || length > best.length)) {
      best = { chain: [item], exact: item.url != null && pathOf(item.url) === path, length };
    }
  }
  return best && { chain: best.chain, exact: best.exact };
}

// Ancestors for the current page, ending before the page itself. The first crumb
// carries the section icon; crumbs repeating the page title are dropped.
export function useNavTrail(title: React.ReactNode): { parents: BreadcrumbItem[]; icon?: LucideIcon } {
  const source = React.useContext(NavTrailContext)?.source;
  const pathname = usePathname();

  return React.useMemo(() => {
    if (!source) return { parents: [] };
    const prefix = `/${source.prefix.replace(/^\/+|\/+$/gu, "")}`;
    const path = (pathname === prefix ? "" : pathname.startsWith(`${prefix}/`) ? pathname.slice(prefix.length + 1) : pathname).replace(/^\/+|\/+$/gu, "");

    const match = findChain(path, source.items);
    if (!match) return { parents: [] };

    const ancestors = match.exact ? match.chain.slice(0, -1) : match.chain;
    const parents = ancestors
      .filter((item) => item.title !== title)
      .map((item, index) => ({
        label: item.title,
        href: item.url ? `${prefix}/${item.url}` : undefined,
        icon: index === 0 ? item.icon ?? undefined : undefined,
      }));
    return { parents, icon: parents.length ? undefined : match.chain[match.chain.length - 1]?.icon ?? match.chain[0]?.icon ?? undefined };
  }, [source, pathname, title]);
}
